import { get, post, put, del } from "@/shared/utils/fetchApi";
import { GetDashboardsParams, GetDashboardDataParams, AddDashboardRequest, UpdateDashboardRequest, DashboardResponse, SaveUserPreferencesRequest, SaveUserPreferencesResponse, UserDashboardPreferencesResponse } from "../types/DashboardRequests";
import { DashboardDataResponse } from "../types/DashboardData";
import { Dashboard } from "../types/Dashboard";

export class DashboardService {
  private static readonly BASE_PATH = "/dashboards";

  static async getUserDashboards(
    params?: GetDashboardsParams,
  ): Promise<Dashboard[]> {
    const queryParams = new URLSearchParams();
    if (params?.workspaceId) {
      queryParams.append("productId", params.workspaceId);
    }
    if (params?.scope) {
      queryParams.append("scope", params.scope);
    }
    if (params?.tabId) {
      queryParams.append("tabId", params.tabId);
    }
    // PBI #78540: superadmin cross-org navigation. Sem orgId no request, o
    // backend defaulta para req.user.organizationId (sessão), e a troca de
    // org via SuperAdminOrgContext fica sem efeito.
    if (params?.orgId && params.scope !== "platform") {
      queryParams.append("orgId", params.orgId);
    }

    const endpoint = `${this.BASE_PATH}/user/dashboards${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

    return await get<Dashboard[]>(endpoint);
  }

  static async getDashboardData(
    dashboardBaseId: string,
    filters?: GetDashboardDataParams,
  ): Promise<DashboardDataResponse> {
    const queryParams = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          queryParams.append(key, String(value));
        }
      });
    }

    const endpoint = `${this.BASE_PATH}/${dashboardBaseId}/data${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

    return await get<DashboardDataResponse>(endpoint);
  }

  static async getAvailableDashboards(
    params?: GetDashboardsParams,
  ): Promise<Dashboard[]> {
    const queryParams = new URLSearchParams();
    if (params?.workspaceId) {
      queryParams.append("productId", params.workspaceId);
    }
    if (params?.scope) {
      queryParams.append("scope", params.scope);
    }
    if (params?.tabId) {
      queryParams.append("tabId", params.tabId);
    }
    // PBI #78540: superadmin cross-org navigation. Mesma regra do
    // getUserDashboards — sem orgId no request, o backend defaulta para
    // req.user.organizationId (org do superadmin) e a lista do workspace
    // de outra org vem vazia.
    if (params?.orgId && params.scope !== "platform") {
      queryParams.append("orgId", params.orgId);
    }

    const endpoint = `${this.BASE_PATH}/user/dashboards/available${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

    return await get<Dashboard[]>(endpoint);
  }

  /**
   * PBI #78540: scope/orgId/productId go via query so the backend writes
   * into user_settings.byScope[<scope>][orgId/productId] instead of
   * clobbering the legacy flat root.
   */
  private static buildScopeQuery(context?: {
    scope?: "platform" | "organization" | "product";
    workspaceId?: string;
    orgId?: string;
  }): string {
    const params = new URLSearchParams();
    const backendScope =
      context?.scope === "platform" ? "superadmin" : context?.scope;
    if (backendScope) params.append("scope", backendScope);
    if (context?.orgId && context?.scope !== "platform") {
      params.append("orgId", context.orgId);
    }
    if (context?.workspaceId && context?.scope === "product") {
      params.append("productId", context.workspaceId);
    }
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }

  static async addDashboard(
    request: AddDashboardRequest,
    context?: {
      scope?: "platform" | "organization" | "product";
      workspaceId?: string;
      orgId?: string;
    },
  ): Promise<DashboardResponse> {
    const endpoint = `${this.BASE_PATH}/user/dashboards${this.buildScopeQuery(context)}`;

    // v2: flat { id, dashboardBaseId }. Wrap to legacy DashboardResponse shape.
    const data = await post<{ id: string; dashboardBaseId: string }>(
      endpoint,
      request,
    );
    return { success: true, data };
  }

  static async updateDashboard(
    dashboardId: string,
    request: UpdateDashboardRequest,
    context?: {
      scope?: "platform" | "organization" | "product";
      workspaceId?: string;
      orgId?: string;
    },
  ): Promise<DashboardResponse> {
    const endpoint = `${this.BASE_PATH}/user/dashboards/${dashboardId}${this.buildScopeQuery(context)}`;

    await put<{ ok: boolean }>(endpoint, request);
    return { success: true };
  }

  static async removeDashboard(
    dashboardId: string,
    context?: {
      scope?: "platform" | "organization" | "product";
      workspaceId?: string;
      orgId?: string;
    },
  ): Promise<DashboardResponse> {
    const endpoint = `${this.BASE_PATH}/user/dashboards/${dashboardId}${this.buildScopeQuery(context)}`;

    await del<{ ok: boolean }>(endpoint);
    return { success: true };
  }

  // Novo endpoint - Salvar preferências completas do usuário.
  // PBI #78540: scope/workspaceId go via query string so the backend can
  // route the write into user_settings.byScope[<scope>][...]. Without these,
  // edits land at the legacy flat root and no per-user override is created.
  static async saveUserPreferences(
    request: SaveUserPreferencesRequest,
    context?: {
      scope?: "platform" | "organization" | "product";
      workspaceId?: string;
      /** Org being viewed. For superadmin cross-org navigation this comes
       *  from SuperAdminOrgContext, not session — different from req.user.org. */
      orgId?: string;
    },
  ): Promise<SaveUserPreferencesResponse> {
    const params = new URLSearchParams();
    // Frontend uses 'platform' for the superadmin scope; backend expects 'superadmin'.
    const backendScope =
      context?.scope === "platform" ? "superadmin" : context?.scope;
    if (backendScope) params.append("scope", backendScope);
    if (context?.orgId && context?.scope !== "platform") {
      params.append("orgId", context.orgId);
    }
    if (context?.workspaceId && context?.scope === "product") {
      params.append("productId", context.workspaceId);
    }
    const qs = params.toString();
    const endpoint = `${this.BASE_PATH}/user/preferences${qs ? `?${qs}` : ""}`;

    await put<{ ok: boolean }>(endpoint, request);
    return { success: true };
  }

  static async getUserPreferences(): Promise<
    UserDashboardPreferencesResponse["data"]
  > {
    const endpoint = `${this.BASE_PATH}/user/preferences`;
    // GET /dashboards/user/preferences retorna prefs flat (com source inline).
    // legacy: { success, data: {...}, source? }.

    const flat = await get<
      UserDashboardPreferencesResponse["data"] & { source?: string }
    >(endpoint);
    // descartar `source` da resposta data — caller atual não usa
    const { source: _src, ...data } = flat as Record<string, unknown>;
    void _src;
    return data as UserDashboardPreferencesResponse["data"];
  }

  /**
   * PBI #78540 — scope-aware preferences read. Returns both the resolved
   * layout and the `source` flag (user | organization | product | settings |
   * empty), so the UI can surface "you're seeing the org default" banners.
   */
  static async getUserPreferencesWithSource(input: {
    scope: "superadmin" | "organization" | "product";
    orgId?: string;
    productId?: string;
  }): Promise<{
    data: UserDashboardPreferencesResponse["data"];
    source: "user" | "organization" | "product" | "settings" | "empty";
  }> {
    const params = new URLSearchParams();
    params.append("scope", input.scope);
    if (input.orgId) params.append("orgId", input.orgId);
    if (input.productId) params.append("productId", input.productId);
    const endpoint = `${this.BASE_PATH}/user/preferences?${params.toString()}`;
    // v2: payload flat com source inline; legacy: { success, data, source }.

    const flat = await get<
      UserDashboardPreferencesResponse["data"] & {
        source?: "user" | "organization" | "product" | "settings" | "empty";
      }
    >(endpoint);
    const { source, ...data } =
      flat as UserDashboardPreferencesResponse["data"] & {
        source?: "user" | "organization" | "product" | "settings" | "empty";
      };
    return {
      data: data as UserDashboardPreferencesResponse["data"],
      source: source ?? "user",
    };
  }

  /**
   * PBI #78540 — Persists the caller's current resolved dashboard layout as
   * the default for a given scope. Authorization is enforced server-side
   * (role=admin in the scope, or superadmin for platform defaults).
   *
   * `asPlatformDefault: true` (superadmin only) writes to the settings table
   * — the org/product-wide fallback that all users without their own prefs
   * end up seeing.
   */
  /**
   * PBI #78540 — Clears the caller's own preferences for the given scope.
   * After this call, the cascade resolver falls through to the inherited
   * default (org/product/settings) — the user sees the default layout
   * again. Any user can reset their own prefs.
   */
  static async resetUserPreferences(input: {
    scope: "superadmin" | "organization" | "product";
    orgId?: string;
    productId?: string;
  }): Promise<{ success: boolean }> {
    const params = new URLSearchParams();
    params.append("scope", input.scope);
    if (input.orgId) params.append("orgId", input.orgId);
    if (input.productId) params.append("productId", input.productId);
    const endpoint = `${this.BASE_PATH}/user/preferences?${params.toString()}`;

    await del<{ ok: boolean }>(endpoint);
    return { success: true };
  }

  static async setAsDefault(input: {
    scope: "superadmin" | "organization" | "product";
    orgId?: string;
    productId?: string;
    asPlatformDefault?: boolean;
  }): Promise<{ savedTo: "organization" | "product" | "settings" }> {
    const endpoint = `${this.BASE_PATH}/set-default`;

    return await post<{ savedTo: "organization" | "product" | "settings" }>(
      endpoint,
      input,
    );
  }
}
